// QR Kiosk v2 — KV2-9 the fail-closed state (§5). The server returns a BYTE-IDENTICAL 404
// for unknown / revoked / disabled / decommissioned / deleted / malformed codes
// (SEC-REQ-B7-7 — no state oracle), so this renders ONE generic state and must never try to
// distinguish or guess which case occurred. Do not add "code not found" vs "disabled"
// wording, and do not add a Retry to the notFound branch (retrying a 404 is noise).
// The screen is never blank and never a forever-spinner (KV2-0 access-denied lesson, bug #10).
import React from "react";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { AccountRoutePaths } from "../../../account/routes";
import { Glyph, GD } from "../shared/glyphs";
import { EscalationPill } from "./NameplateLadder";
import { NameplateErrorKind } from "./nameplateApi";

export interface NameplateUnavailableProps {
  kind: NameplateErrorKind;
  onRetry: () => void;
}

export function NameplateUnavailable({ kind, onRetry }: NameplateUnavailableProps) {
  const { translate } = useTranslation("public");
  const navigate = useNavigate();

  const copy =
    kind === "rateLimited"
      ? { title: translate("kioskV2NameplateRateLimitedTitle"), body: translate("kioskV2NameplateRateLimitedBody") }
      : kind === "network"
      ? { title: translate("kioskV2NameplateNetworkTitle"), body: translate("kioskV2NameplateNetworkBody") }
      : { title: translate("kioskV2NameplateUnavailableTitle"), body: translate("kioskV2NameplateUnavailableBody") };

  // Retry ONLY for the recoverable kinds. A 404 is final by design.
  const canRetry = kind !== "notFound";

  return (
    <div className="kv2-fade" style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      {/* Same TOP slot as the plate page, same pill weight — the two states must not read as
          different products. No caption: there is no machine here to have access to, and no
          return URL for the same reason (§5).
          BOTH access paths are offered (owner ruling 2026-08-13): a sticker scanned on a
          switched-off plate must still route a legitimate visitor in, and sign-in is not the
          only door — a guest code is. This does NOT weaken the uniform-404 rule (SEC-REQ-B7-7):
          the pills are STATIC and identical for every failure cause (unknown / revoked /
          disabled / decommissioned / malformed / rate-limited / offline), so they reveal
          nothing about whether the scanned code exists. Do not "harden" them back out. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
        <EscalationPill
          tone="slate"
          label={translate("kioskV2NameplateSignIn")}
          onClick={() => navigate(AccountRoutePaths.signIn)}
        />
        {/* Same destination shape as the plate-ON pill (NameplateLadder `goGuest`), minus the
            machine: with no code resolved there is no internalNumber to lock, so this lands on
            the unscoped guest landing, which asks for machine number + PIN. `?entry=pin` is
            carried for parity with the plate-ON pill — the landing already opens in PIN mode
            when there is no #code, and the param keeps the intent explicit if that changes. */}
        <EscalationPill
          tone="plain"
          label={translate("kioskV2NameplateGuestCode")}
          onClick={() => navigate("/public-v2/guest?entry=pin")}
        />
      </div>

      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          boxShadow: "var(--shadow)",
          padding: "26px 20px 22px",
          textAlign: "center",
        }}
      >
        <span
          style={{
            width: 46,
            height: 46,
            borderRadius: 999,
            background: "var(--bg-soft)",
            border: "1px solid var(--border-soft)",
            color: "var(--faint)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <Glyph d={GD.qr} size={22} />
        </span>

        <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3 }}>{copy.title}</div>
        <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 8, lineHeight: 1.55 }}>{copy.body}</div>

        {/* Retry stays a card-level action (recoverable kinds only) — the top slot is the
            escalation lane and holds nothing but the pill. */}
        {canRetry && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <EscalationPill tone="plain" label={translate("kioskV2Retry")} onClick={onRetry} />
          </div>
        )}
      </div>
    </div>
  );
}

export default NameplateUnavailable;
